/**
 * Back office → Website → Media (D-108): the agency replaces the default videos and photos of its
 * public site (hero, each service, sections). The file is re-encoded on the server (720p + 540p,
 * poster, several photo sizes); until it is ready, the site keeps the default medium.
 */

import {
  getWebsiteMediaListQueryKey,
  type SiteMedia,
  useWebsiteMediaCreate,
  useWebsiteMediaDestroy,
  useWebsiteMediaList,
} from "@agencyos/api-client";
import { Badge, type BadgeTone, Button, GlassCard, Skeleton } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { ImageUp, Trash2 } from "lucide-react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { fieldErrors, useCanEditSettings } from "../../lib/settings";
import "./site-media.css";

/** Places of the site that show a medium (same keys as apps/website/media.py → SLOTS). */
const SLOTS = [
  "home-hero", "home-canada", "home-russie", "home-academy", "home-voyage", "home-cameroun",
  "dest-canada", "dest-russie", "dest-visa", "dest-billets", "dest-langues",
  "service-ee", "service-ev", "service-vv", "service-ru", "service-bl", "service-tcf",
  "service-other", "offer", "packages", "simulator", "agency", "contact", "legal", "notfound",
] as const; // prettier-ignore

const TONES: Record<string, BadgeTone> = {
  pending: "info",
  processing: "info",
  ready: "success",
  failed: "danger",
};
const ACCEPT = "video/mp4,video/quicktime,video/webm,image/jpeg,image/png,image/webp,image/avif";

function SlotRow({ slot, row, canEdit }: { slot: string; row?: SiteMedia; canEdit: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: getWebsiteMediaListQueryKey() });
  const upload = useWebsiteMediaCreate({ mutation: { onSuccess: refresh } });
  const remove = useWebsiteMediaDestroy({ mutation: { onSuccess: refresh } });
  const input = useRef<HTMLInputElement>(null);
  const error = fieldErrors(upload.error).original ?? fieldErrors(upload.error).slot;
  const label = t(`websiteMedia.slot.${slot}`);
  return (
    <li className="site-media__row">
      <div className="site-media__thumb" aria-hidden="true">
        {row?.preview_url ? <img src={row.preview_url} alt="" /> : <ImageUp size={20} strokeWidth={1.75} />}
      </div>
      <div className="stack-xs">
        <strong>{label}</strong>
        {row ? (
          <span className="row-xs">
            <Badge tone={TONES[row.status] ?? "default"}>{t(`websiteMedia.status.${row.status}`)}</Badge>
            <span className="muted">{t(`websiteMedia.kind.${row.kind}`)}</span>
            {row.bytes ? <span className="muted ao-num">{Math.round(row.bytes / 1024)} Ko</span> : null}
          </span>
        ) : (
          <span className="muted">{t("websiteMedia.default")}</span>
        )}
        {row?.status === "failed" && row.error ? <span className="ao-field__error">{row.error}</span> : null}
        {error ? (
          <span role="alert" className="ao-field__error">
            {error}
          </span>
        ) : null}
      </div>
      {canEdit ? (
        <div className="row-xs">
          <input
            ref={input}
            type="file"
            accept={ACCEPT}
            className="visually-hidden"
            aria-label={t("websiteMedia.choose", { place: label })}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) upload.mutate({ data: { slot, original: file } });
              e.target.value = "";
            }}
          />
          <Button variant="secondary" size="sm" disabled={upload.isPending} onClick={() => input.current?.click()}>
            {upload.isPending ? t("websiteMedia.sending") : row ? t("websiteMedia.replace") : t("websiteMedia.upload")}
          </Button>
          {row ? (
            <Button
              variant="ghost"
              size="sm"
              icon={<Trash2 size={16} strokeWidth={1.75} aria-hidden="true" />}
              disabled={remove.isPending}
              onClick={() => remove.mutate({ id: row.id })}
            >
              {t("websiteMedia.restore")}
            </Button>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

export function SiteMediaTab() {
  const { t } = useTranslation();
  const canEdit = useCanEditSettings();
  const queryClient = useQueryClient();
  const list = useWebsiteMediaList();
  const rows = list.data?.status === 200 ? list.data.data : [];
  const busy = rows.some((r) => r.status === "pending" || r.status === "processing");
  useEffect(() => {
    if (!busy) return undefined;
    const timer = window.setInterval(
      () => queryClient.invalidateQueries({ queryKey: getWebsiteMediaListQueryKey() }),
      5000,
    );
    return () => window.clearInterval(timer);
  }, [busy, queryClient]);
  if (list.isPending) return <Skeleton style={{ height: 240 }} />;
  const bySlot = new Map(rows.map((r) => [r.slot, r]));
  return (
    <GlassCard as="section" className="stack">
      <h2 className="ao-card__title">{t("websiteMedia.title")}</h2>
      <p className="muted">{t("websiteMedia.intro")}</p>
      <ul className="site-media">
        {SLOTS.map((slot) => (
          <SlotRow key={slot} slot={slot} row={bySlot.get(slot)} canEdit={canEdit} />
        ))}
      </ul>
    </GlassCard>
  );
}
